import gsap from 'gsap'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { useDispensaryContext } from '../../../../lib/dispensaries'
import {
  desktopMenuWrapperTop$,
  setParallaxEnabled,
  setParallaxPos,
} from '../../../../lib/header/parallax'
import { dispensaryImageUrlFor } from '../../../../lib/sanity'
import { dispensaryLogoCdn$ } from '../../../../lib/sanity-dispensaries/query'
import MediaQueries from '../../../../utils/MediaQueries'
import Cart from '../../cart/Cart'
import DesktopMenuWrapper from '../DesktopMenuWrapper'
import { MobileMenu } from '../MobileMenu'
import * as Styled from './styled'

import printLogoImage from '../../../../img/logo-dark.svg'

export interface HeaderProps {
  className?: string
  showAccountMenu: boolean
}

const Header: React.FC<HeaderProps> = ({ className, showAccountMenu }) => {
  const scrollListenerRef = useRef<() => void>()
  const navBackgroundRef = useRef<HTMLDivElement>(null)
  const dispensary = useDispensaryContext()
  const [dispensaryLogoSrc, setDispensaryLogoSrc] = useState<string | null>(null)
  const isDispensaryLogo = !!dispensaryLogoSrc

  useEffect(() => {
    if (!dispensary) {
      return
    }
    // create subscription instead (of useDispensaryLogoQuery()) since we only want a
    // subscription on dispensaries
    const subscription = dispensaryLogoCdn$.subscribe((value) => {
      setDispensaryLogoSrc(
        value._tag === 'Right' && value.data?.logoImageUrl
          ? dispensaryImageUrlFor(value.data.logoImageUrl).height(90).dpr(3).auto('format').url()
          : null,
      )
    })
    return () => subscription.unsubscribe()
  }, [dispensary])

  useEffect(() => {
    // apply parallax scrolling effect to header and update search container position under nav
    const updateComponentPositionsForLargeViewport = () => {
      if (isDispensaryLogo) {
        setParallaxPos(0)
        return
      }
      const parallaxPos = -window.pageYOffset / 2
      setParallaxPos(parallaxPos)
    }

    // set search container position to top of screen for small viewports
    const updateComponentPositionsForSmallViewport = () => {
      setParallaxPos(0)
    }

    const handleLargeViewportScroll = updateComponentPositionsForLargeViewport

    // listener should only be added when large media query matches
    const addScrollEventListener = () => {
      removeScrollEventListener()
      scrollListenerRef.current = handleLargeViewportScroll
      document.addEventListener('scroll', scrollListenerRef.current)
    }

    const removeScrollEventListener = () => {
      if (scrollListenerRef.current) {
        document.removeEventListener('scroll', scrollListenerRef.current)
        scrollListenerRef.current = undefined
      }
    }

    const mq = MediaQueries.getLarge()
    const largeMediaQueryHandler = (e: MediaQueryListEvent | { matches: boolean }) => {
      if (e.matches) {
        setParallaxEnabled(true)
        addScrollEventListener()
        updateComponentPositionsForLargeViewport()
      } else {
        setParallaxEnabled(false)
        removeScrollEventListener()
        updateComponentPositionsForSmallViewport()
      }
    }
    // run handler initially prior to adding listener
    largeMediaQueryHandler({ matches: mq.matches })
    // use addListener for Safari
    mq.addListener(largeMediaQueryHandler)

    // remove listener on cleanup
    return () => {
      // use removeListener for Safari
      mq.removeListener(largeMediaQueryHandler)
      removeScrollEventListener()
    }
  }, [isDispensaryLogo])

  useEffect(() => {
    const subscription = desktopMenuWrapperTop$.subscribe((desktopMenuWrapperTop) => {
      if (navBackgroundRef.current) {
        gsap.set(navBackgroundRef.current, { top: desktopMenuWrapperTop })
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  return (
    <header className={className}>
      <Styled.NavBackground
        aria-hidden="true"
        ref={navBackgroundRef}
        isDispensaryLogo={isDispensaryLogo}
      />
      <MobileMenu showAccountMenu={showAccountMenu} />
      <DesktopMenuWrapper />
      <Cart />
      <Styled.PrintLogoImage src={dispensaryLogoSrc || printLogoImage} />
    </header>
  )
}

const StyledHeader = styled(Header)`
  color: ${({ theme }) => theme.colors.grayscale.white};
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  z-index: 1000;
  transition: all 0.5s;
  box-sizing: border-box;
  @media print {
    position: static;
  }
`

export { StyledHeader as Header }
